/**
 * Podium — les trois premiers du classement sur une scène sombre : faisceau
 * de projecteur, trois blocs de pierre 3D au chiffre gravé, l'avatar, le nom
 * et le montant au-dessus de chacun, le trophée qui flotte sur le premier.
 * Les blocs montent du sol l'un après l'autre (3, 2, puis 1) avec un petit
 * impact à l'arrivée — la scène tressaille quand le 1 se pose — et le montant
 * grimpe de 0 dès que son bloc est posé.
 *
 * Trois MARCHES (centre, gauche, droite) prises dans l'ordre des `entrees` ;
 * le chiffre gravé est le RANG de la personne — deux 1ers ex æquo occupent les
 * marches 1 et 2, tous deux gravés « 1 » (même règle que le classement des
 * chatteurs). Une marche sans personne montre « Place libre ». Quand le
 * classement bouge en direct, la personne qui change de marche sort et entre
 * en douceur (clé = `id`, sinon rang + nom).
 *
 * La scène garde son format (1000 × 700 : le podium 1000 × 620 et le ciel
 * au-dessus du premier) ou prend la hauteur qu'on lui donne (`hauteur`, ou la
 * hauteur CSS de la boîte). Le podium se cale en bas, centré, à la plus grande
 * échelle où la pile du premier (trophée, tuile, nom, montant, lignes de
 * dessous) tient sous le haut de la scène ; la lampe descend du haut de la
 * scène, quel que soit le ciel. Mesuré au montage puis à chaque
 * redimensionnement, les personnes sont posées en pixels. Blocs et lumière =
 * SVG ; les personnes = HTML par-dessus. Jamais d'image. Le texte est blanc
 * ou gris ; la couleur de marque ne touche que le badge « Toi ».
 * Le détail et le résumé sous une marche (`sousMarche`) ne dépassent jamais la
 * part VISIBLE de leur bloc : centrés, ils passent sur deux lignes équilibrées,
 * et la pile compte les lignes qu'ils prennent vraiment (mesurées).
 * Les tailles des personnes suivent la largeur du podium : jusqu'à 550 px,
 * celles d'avant le 27-10-2026 (ce qui marchait à 548 px marche pareil) ; au-
 * delà elles grandissent plus vite que les blocs — à 1000 px, la tuile du
 * premier fait 120 px, son nom 21 px, son montant 29 px (Kayla, 27-10 : « on
 * pourrait mettre ça bien plus grand et bien plus visible »).
 * `prefers-reduced-motion` → tout est en place d'emblée.
 */
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from "framer-motion";
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";

import { cn } from "../utils";
import { Trophy } from "../icons";

import "./classement.css";
import { formatAvec } from "./format";
import { Tuile } from "./Tuile";
import { cleDe, type EntreeClassement, type OptionsFormat } from "./types";
import { useCountUp } from "./useCountUp";

export interface PodiumProps extends OptionsFormat {
  /** Les lignes du classement, dans l'ordre : la 1re occupe le bloc du centre,
   *  la 2e celui de gauche, la 3e celui de droite. Au-delà de trois, ignorées. */
  entrees: EntreeClassement[];
  /** Texte d'une marche vide (défaut « Place libre ») ; `null` = rien. */
  placeLibre?: string | null;
  /** Sous chaque marche, occupée ou non : la prime de la place, par exemple. */
  sousMarche?: (marche: 1 | 2 | 3, entree?: EntreeClassement) => ReactNode;
  /** La version abrégée de `sousMarche` (marche occupée), prise seulement quand
   *  la longue ne tient pas en deux lignes dans la largeur de son bloc. */
  sousMarcheCourt?: (marche: 1 | 2 | 3, entree: EntreeClassement) => ReactNode;
  /** Les montants sont blancs ; `vert` = un vert doux de sens (positif). */
  couleurMontants?: "blanc" | "vert";
  /** Hauteur imposée (px ou CSS) : le podium se cale en bas, centré, à
   *  l'échelle. Sans elle, la scène garde son format 1000 × 700 — ou la
   *  hauteur que lui donne sa `className` (`h-full`, `max-h-[560px]`…). */
  hauteur?: number | string;
  /** Largeur maximale de la scène (px) — avec `hauteur`, pour qu'empilé sur un
   *  panneau bas et large, le podium ne s'étire pas sur toute la largeur
   *  (Kayla, 27-10 : « quand il prend toute la longueur, c'est horrible »). */
  largeurMax?: number;
  onClick?: (entree: EntreeClassement) => void;
  className?: string;
}

const VB_W = 1000;
const VB_H = 620;
/** La scène sans hauteur imposée : le podium et le ciel au-dessus (`.cl-scene`). */
const SCENE_H = 700;
/** Le sol : le bas des blocs ; dessous, leur reflet sur un sol brillant. */
const SOL = 556;
/** Hauteur apparente du dessus (la face claire). */
const PROF = 34;
/** Retrait des coins arrière du dessus. */
const RETRAIT = 22;

type Marche = 1 | 2 | 3;

/** Les lignes que prennent, une fois posés, le détail et le résumé d'une marche. */
interface Lignes {
  detail: number;
  sous: number;
}
const UNE_LIGNE: Lignes = { detail: 1, sous: 1 };

interface Bloc {
  marche: Marche;
  x: number;
  w: number;
  h: number;
  /** Retard de départ (s) — le 3 part en premier, le 1 arrive en dernier. */
  retard: number;
}

/** Ordre du tableau = ordre de dessin : le 1 passe devant ses voisins. */
const BLOCS: readonly Bloc[] = [
  { marche: 2, x: 95, w: 280, h: 215, retard: 0.3 },
  { marche: 3, x: 625, w: 280, h: 170, retard: 0.08 },
  { marche: 1, x: 355, w: 290, h: 285, retard: 0.55 },
];

const RESSORT = { type: "spring", stiffness: 230, damping: 17, mass: 0.9 } as const;
const COURBE = [0.2, 0.7, 0.2, 1] as const;
/** Quand le bloc touche le sol après son départ (le ressort passe par zéro). */
const IMPACT = 0.27;

/** La boîte de la scène, mesurée — tout de suite au montage, puis à chaque
 *  redimensionnement. 🪤 Le panneau navigateur de l'app ne rappelle JAMAIS un
 *  ResizeObserver (il ne composite pas) : la mesure directe au montage et sur
 *  `resize` est ce qui fait voir le podium là aussi. */
function useBoite(): { ref: RefObject<HTMLDivElement | null>; w: number; h: number } {
  const ref = useRef<HTMLDivElement | null>(null);
  const [taille, setTaille] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const mesurer = () => {
      const { width, height } = el.getBoundingClientRect();
      setTaille((t) => (t.w === width && t.h === height ? t : { w: width, h: height }));
    };
    mesurer();
    window.addEventListener("resize", mesurer);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(mesurer) : null;
    ro?.observe(el);
    return () => {
      window.removeEventListener("resize", mesurer);
      ro?.disconnect();
    };
  }, []);
  return { ref, w: taille.w, h: taille.h };
}

/** La géométrie rendue : la largeur du podium (`sw`), sa hauteur, son décalage à gauche. */
interface Cadre {
  sw: number;
  sh: number;
  ox: number;
  /** Faux tant que la boîte n'a pas de taille (tests, onglet caché) : on ne
   *  fait alors ni grimper les montants ni attendre un retard — tout est posé. */
  mesuree: boolean;
}

export function Podium({
  entrees,
  placeLibre = "Place libre",
  sousMarche,
  sousMarcheCourt,
  couleurMontants = "blanc",
  hauteur,
  largeurMax,
  format = "montant",
  devise = "EUR",
  suffixe,
  formater,
  onClick,
  className,
}: PodiumProps) {
  const reduit = useReducedMotion() ?? false;
  const { ref, w, h } = useBoite();
  // Une boîte lue à 0 px (tests, onglet caché) n'efface pas le podium : les
  // personnes se posent sur la géométrie de la scène, et la vraie mesure suit.
  const mesuree = w > 0 && h > 0;
  const bw = mesuree ? w : VB_W;
  const bh = mesuree ? h : SCENE_H;
  const surMarche = (marche: Marche): EntreeClassement | undefined => entrees[marche - 1];
  const avecSous = sousMarche !== undefined;
  // Mesurées après la pose : un résumé passé sur deux lignes grandit la pile.
  const [lignes, setLignes] = useState<Record<Marche, Lignes>>({ 1: UNE_LIGNE, 2: UNE_LIGNE, 3: UNE_LIGNE });
  const surLignes = (marche: Marche, l: Lignes) =>
    setLignes((avant) =>
      avant[marche].detail === l.detail && avant[marche].sous === l.sous ? avant : { ...avant, [marche]: l },
    );
  const sw = largeurQuiTient(bw, bh, (s) => {
    const shS = (s * VB_H) / VB_W;
    let pile = 0;
    for (const bloc of BLOCS) {
      const haut = hauteurPersonne(
        bloc.marche,
        s,
        surMarche(bloc.marche),
        placeLibre !== null,
        avecSous,
        lignes[bloc.marche],
      );
      pile = Math.max(pile, piedDe(bloc, shS) + haut);
    }
    return pile + 4 + s * 0.008;
  });
  const sh = (sw * VB_H) / VB_W;
  const cadre: Cadre = { sw, sh, ox: (bw - sw) / 2, mesuree };
  // Le haut de la scène dans le repère du dessin (≤ 0) : la lampe y est accrochée.
  const yHaut = Math.min(0, -((bh - sh) / sh) * VB_H);
  const impactDuPremier = BLOCS.find((b) => b.marche === 1)!.retard + IMPACT;

  const style: CSSProperties = {
    ...(hauteur !== undefined ? { height: hauteur, aspectRatio: "auto" } : {}),
    ...(largeurMax !== undefined ? { maxWidth: largeurMax } : {}),
    ["--cl-sw" as string]: px(sw),
  };

  return (
    <div ref={ref} className={cn("cl-root cl-scene", className)} style={style} role="list" aria-label="Podium">
      {/* La scène tressaille quand le premier bloc se pose. */}
      <motion.div
        className="absolute inset-0"
        initial={false}
        animate={{ y: reduit ? 0 : [0, 0, 3, -2, 1, 0] }}
        transition={{ delay: impactDuPremier, duration: 0.42, ease: "easeOut" }}
      >
        <svg
          className="cl-scene-svg"
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="xMidYMax meet"
          aria-hidden="true"
          // Mesurée : le dessin prend la largeur du podium, posé en bas, centré.
          style={mesuree ? { left: px(cadre.ox), top: px(bh - sh), width: px(sw), height: px(sh) } : undefined}
        >
          <defs>
            {/* En repère du dessin : au-dessus de −30, le faisceau garde sa
                lumière d'en haut jusqu'au plafond de la scène. */}
            <linearGradient id="cl-faisceau" gradientUnits="userSpaceOnUse" x1="0" y1="-30" x2="0" y2="600">
              <stop offset="0" style={{ stopColor: "var(--ui-lumiere)", stopOpacity: 0.34 }} />
              <stop offset="0.55" style={{ stopColor: "var(--ui-lumiere)", stopOpacity: 0.09 }} />
              <stop offset="1" style={{ stopColor: "var(--ui-lumiere)", stopOpacity: 0 }} />
            </linearGradient>
            <radialGradient id="cl-lampe" cx="0.5" cy="0" r="0.6">
              <stop offset="0" style={{ stopColor: "#fff3d6", stopOpacity: 0.55 }} />
              <stop offset="0.45" style={{ stopColor: "var(--ui-lumiere)", stopOpacity: 0.18 }} />
              <stop offset="1" style={{ stopColor: "var(--ui-lumiere)", stopOpacity: 0 }} />
            </radialGradient>
            <radialGradient id="cl-lampe-sol" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" style={{ stopColor: "var(--ui-lumiere)", stopOpacity: 0.26 }} />
              <stop offset="1" style={{ stopColor: "var(--ui-lumiere)", stopOpacity: 0 }} />
            </radialGradient>
            <linearGradient id="cl-dessus" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" style={{ stopColor: "var(--cl-pierre-dessus-arriere)" }} />
              <stop offset="1" style={{ stopColor: "var(--cl-pierre-dessus-avant)" }} />
            </linearGradient>
            <linearGradient id="cl-face" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" style={{ stopColor: "var(--cl-pierre-face-haut)" }} />
              <stop offset="1" style={{ stopColor: "var(--cl-pierre-face-bas)" }} />
            </linearGradient>
            <linearGradient id="cl-reflet" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#ffffff" stopOpacity="0.07" />
              <stop offset="0.3" stopColor="#ffffff" stopOpacity="0" />
              <stop offset="0.7" stopColor="#000000" stopOpacity="0" />
              <stop offset="1" stopColor="#000000" stopOpacity="0.22" />
            </linearGradient>
            <linearGradient id="cl-reflet-fondu" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
            <mask
              id="cl-masque-reflet"
              maskUnits="userSpaceOnUse"
              x="0"
              y={SOL}
              width={VB_W}
              height={VB_H - SOL}
            >
              <rect x="0" y={SOL} width={VB_W} height={VB_H - SOL} fill="url(#cl-reflet-fondu)" />
            </mask>
            <filter id="cl-flou" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="16" />
            </filter>
            <filter id="cl-flou-sol" x="-20%" y="-200%" width="140%" height="500%">
              <feGaussianBlur stdDeviation="9" />
            </filter>
            <filter id="cl-ombre-bloc" x="-20%" y="-10%" width="140%" height="130%">
              <feDropShadow dx="0" dy="6" stdDeviation="14" floodColor="#000" floodOpacity="0.55" />
            </filter>
          </defs>

          {/* La lampe et son faisceau — derrière tout le reste. */}
          <motion.g
            className="cl-faisceau"
            initial={reduit ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            <polygon
              points={`455,${(yHaut - 30).toFixed(1)} 545,${(yHaut - 30).toFixed(1)} 820,600 180,600`}
              fill="url(#cl-faisceau)"
              filter="url(#cl-flou)"
            />
            <ellipse cx="500" cy={yHaut.toFixed(1)} rx="230" ry="110" fill="url(#cl-lampe)" />
          </motion.g>

          {/* Le sol : la tache de lumière du faisceau, l'horizon, l'ombre des blocs. */}
          <ellipse cx="500" cy={SOL} rx="320" ry="36" fill="url(#cl-lampe-sol)" />
          <line x1="0" y1={SOL} x2={VB_W} y2={SOL} stroke="#ffffff" strokeOpacity="0.05" />
          <ellipse
            cx="500"
            cy={SOL + 3}
            rx="470"
            ry="16"
            fill="#000"
            fillOpacity="0.6"
            filter="url(#cl-flou-sol)"
          />

          {/* Les reflets, dans un sol brillant — ils descendent quand le bloc monte. */}
          <g mask="url(#cl-masque-reflet)" opacity="0.26">
            {BLOCS.map((bloc) => (
              <RefletBloc key={bloc.marche} bloc={bloc} reduit={reduit} />
            ))}
          </g>

          {BLOCS.map((bloc) => (
            <BlocPodium
              key={bloc.marche}
              bloc={bloc}
              chiffre={surMarche(bloc.marche)?.rang ?? bloc.marche}
              reduit={reduit}
            />
          ))}
        </svg>

        {sw > 0 &&
          BLOCS.map((bloc) => {
            const entree = surMarche(bloc.marche);
            return (
              <AnimatePresence key={bloc.marche}>
                {entree ? (
                  <Personne
                    key={cleDe(entree)}
                    bloc={bloc}
                    cadre={cadre}
                    entree={entree}
                    reduit={reduit}
                    format={format}
                    devise={devise}
                    suffixe={suffixe}
                    formater={formater}
                    sousMarche={sousMarche}
                    sousMarcheCourt={sousMarcheCourt}
                    couleurMontants={couleurMontants}
                    onClick={onClick}
                    onLignes={surLignes}
                  />
                ) : placeLibre !== null ? (
                  <PlaceLibre
                    key="libre"
                    bloc={bloc}
                    cadre={cadre}
                    reduit={reduit}
                    texte={placeLibre}
                    sous={sousMarche?.(bloc.marche)}
                    onLignes={surLignes}
                  />
                ) : null}
              </AnimatePresence>
            );
          })}
      </motion.div>
    </div>
  );
}

/** Le reflet d'un bloc sous la ligne du sol : la face, renversée, qui s'éteint
 *  en descendant. Il part du haut (vers −360) pendant que le bloc monte. */
function RefletBloc({ bloc, reduit }: { bloc: Bloc; reduit: boolean }) {
  const { x, w, h, retard } = bloc;
  const haut = SOL - h;
  return (
    <motion.g
      initial={reduit ? false : { y: -360 }}
      animate={{ y: 0 }}
      transition={{ ...RESSORT, delay: retard }}
    >
      <g transform={`matrix(1 0 0 -1 0 ${2 * SOL})`}>
        <rect x={x} y={haut} width={w} height={h} rx={7} fill="url(#cl-face)" />
        <rect x={x} y={haut} width={w} height={h} rx={7} fill="url(#cl-reflet)" />
      </g>
    </motion.g>
  );
}

function BlocPodium({ bloc, chiffre, reduit }: { bloc: Bloc; chiffre: number; reduit: boolean }) {
  const { marche, x, w, h, retard } = bloc;
  const haut = SOL - h;
  const cx = x + w / 2;
  const dessus = [
    `${x},${haut + 2}`,
    `${x + w},${haut + 2}`,
    `${x + w - RETRAIT},${haut - PROF}`,
    `${x + RETRAIT},${haut - PROF}`,
  ].join(" ");
  const tailleChiffre = Math.round(h * 0.58);
  const yChiffre = haut + h * 0.5 + tailleChiffre * 0.36;

  return (
    <>
      {/* L'impact : une onde au sol quand le bloc se pose. */}
      {!reduit && (
        <motion.ellipse
          cx={cx}
          cy={SOL}
          ry={5}
          fill="#ffffff"
          initial={{ rx: 30, opacity: 0 }}
          animate={{ rx: [30, w * 0.62, w * 0.72], opacity: [0, 0.38, 0] }}
          transition={{ delay: retard + IMPACT, duration: 0.55, ease: "easeOut" }}
        />
      )}
      <motion.g
        filter={marche === 1 ? "url(#cl-ombre-bloc)" : undefined}
        initial={reduit ? false : { y: 360 }}
        animate={{ y: 0 }}
        transition={{ ...RESSORT, delay: retard }}
      >
        <rect x={x} y={haut} width={w} height={h + 1} rx={7} fill="url(#cl-face)" />
        <rect x={x} y={haut} width={w} height={h + 1} rx={7} fill="url(#cl-reflet)" />
        <polygon points={dessus} fill="url(#cl-dessus)" />
        <line
          x1={x + 1}
          y1={haut + 2}
          x2={x + w - 1}
          y2={haut + 2}
          stroke="#ffffff"
          strokeOpacity="0.28"
          strokeWidth="1.5"
        />
        {/* Le chiffre gravé : un trait clair sous l'arête, le creux par-dessus. */}
        <text
          x={cx}
          y={yChiffre + 3}
          textAnchor="middle"
          fontSize={tailleChiffre}
          fontWeight={700}
          fontFamily="Almarena Neue, Bricolage Grotesque, system-ui, sans-serif"
          fill="#ffffff"
          fillOpacity="0.09"
        >
          {chiffre}
        </text>
        <text
          x={cx}
          y={yChiffre}
          textAnchor="middle"
          fontSize={tailleChiffre}
          fontWeight={700}
          fontFamily="Almarena Neue, Bricolage Grotesque, system-ui, sans-serif"
          style={{ fill: "var(--cl-grave)" }}
        >
          {chiffre}
        </text>
      </motion.g>
    </>
  );
}

const px = (n: number): string => `${n.toFixed(1)}px`;

/** Du bas de la scène au pied d'une personne (px) : l'arrière du dessus de son bloc. */
function piedDe(bloc: Bloc, sh: number): number {
  const arriere = SOL - bloc.h - PROF;
  return ((VB_H - arriere) / VB_H) * sh + sh * 0.014;
}

/** Position d'une personne au-dessus de sa marche, en pixels de la scène. */
function placeDe(bloc: Bloc, cadre: Cadre): { left: string; bottom: string } {
  return {
    left: px(cadre.ox + ((bloc.x + bloc.w / 2) / VB_W) * cadre.sw),
    bottom: px(piedDe(bloc, cadre.sh)),
  };
}

/** Jusqu'à cette largeur de podium (px), les tailles d'avant le 27-10-2026. */
const COUDE = 550;

/** Une taille qui suit la largeur du podium : pente `k` jusqu'au coude, `kGrand`
 *  au-delà (les personnes grandissent alors plus vite que les blocs), bornée. */
function borne(sw: number, k: number, kGrand: number, min: number, max: number): number {
  const brut = sw <= COUDE ? sw * k : COUDE * k + (sw - COUDE) * kGrand;
  return Math.min(max, Math.max(min, brut));
}

/** Les interlignes posés sur les textes d'une personne : sa hauteur se calcule. */
const INTERLIGNE = { nom: 1.2, petit: 1.3 } as const;

/** Les tailles d'une personne (px) pour un podium de largeur `sw`. */
function taillesDe(sw: number) {
  return {
    tuile1: borne(sw, 0.094, 0.152, 40, 140),
    tuile: borne(sw, 0.078, 0.127, 34, 112),
    trophee: borne(sw, 0.036, 0.049, 14, 50),
    nom: borne(sw, 0.016, 0.027, 10, 24),
    sousNom: borne(sw, 0.012, 0.0187, 9, 17),
    etiquette: borne(sw, 0.01, 0.0144, 8, 13),
    montant: borne(sw, 0.02, 0.04, 11, 34),
    // Le résumé sous une marche ne grandit pas autant : à côté, il mordrait sur le bloc 1.
    petit: borne(sw, 0.0125, 0.0145, 9, 16),
    margeNom: borne(sw, 0.003, 0.003, 1, 4),
    /** L'écart entre deux éléments de la pile (`gap` de `.cl-personne`). */
    ecart: sw * 0.005,
  };
}

/** La hauteur de la pile d'une personne (px) : ce que `Personne` / `PlaceLibre`
 *  rendent, ligne par ligne. Sert à trouver l'échelle où tout tient. */
function hauteurPersonne(
  marche: Marche,
  sw: number,
  entree: EntreeClassement | undefined,
  libre: boolean,
  avecSous: boolean,
  lignes: Lignes,
): number {
  const t = taillesDe(sw);
  const tuile = marche === 1 ? t.tuile1 : t.tuile;
  const petite = t.petit * INTERLIGNE.petit;
  const morceaux: number[] = [];
  if (entree) {
    if (marche === 1) morceaux.push(t.trophee);
    // « Toi » dépasse du haut de la tuile (−12 %) ; au premier, il reste sous le trophée.
    morceaux.push(tuile + (entree.estMoi && marche !== 1 ? tuile * 0.12 : 0));
    morceaux.push(t.nom * INTERLIGNE.nom + t.margeNom);
    if (entree.sousNom) morceaux.push(t.sousNom * INTERLIGNE.nom);
    if (entree.exAequo) morceaux.push(t.etiquette * INTERLIGNE.nom);
    morceaux.push(t.montant);
    if (entree.detail) morceaux.push(petite * lignes.detail);
    if (avecSous) morceaux.push(petite * lignes.sous);
  } else if (libre) {
    morceaux.push(tuile, t.nom * INTERLIGNE.nom + t.margeNom);
    if (avecSous) morceaux.push(petite * lignes.sous);
  }
  if (morceaux.length === 0) return 0;
  return morceaux.reduce((a, b) => a + b, 0) + (morceaux.length - 1) * t.ecart;
}

/** La largeur du podium : la plus grande où `besoin` (la hauteur prise par la
 *  plus haute pile) tient dans la boîte. Les tailles suivent la largeur, d'où
 *  la dichotomie. Une boîte trop basse pour tout garde 40 % de la largeur
 *  possible : la pile déborde plutôt que le podium disparaisse. */
function largeurQuiTient(bw: number, bh: number, besoin: (sw: number) => number): number {
  const max = Math.min(bw, (bh * VB_W) / VB_H);
  if (besoin(max) <= bh) return max;
  let bas = max * 0.4;
  let haut = max;
  if (besoin(bas) > bh) return bas;
  for (let i = 0; i < 18; i++) {
    const milieu = (bas + haut) / 2;
    if (besoin(milieu) <= bh) bas = milieu;
    else haut = milieu;
  }
  return bas;
}

/** La largeur permise à une ligne de dessous (unités du dessin) : la part du
 *  bloc qu'on voit — le 1 passe devant ses voisins et mange 20 de leur bord,
 *  retirés des deux côtés pour rester centré sur sa marche — moins une marge
 *  de chaque côté : une ligne qui touche la jointure des blocs a l'air d'y mordre. */
function largeurDuResume(bloc: Bloc): number {
  const premier = BLOCS.find((b) => b.marche === 1)!;
  const recouvre =
    bloc.marche === 1
      ? 0
      : Math.max(0, Math.min(bloc.x + bloc.w, premier.x + premier.w) - Math.max(bloc.x, premier.x));
  return bloc.w - 2 * recouvre - 2 * 14;
}

/** Une ligne de dessous (détail, résumé) : jamais plus large que son bloc
 *  (`largeurDuResume`), centrée dessus (elle peut déborder de la colonne de la
 *  personne, plus étroite), sur deux lignes équilibrées s'il le faut. */
function styleDessous(bloc: Bloc, sw: number, taille: number): CSSProperties {
  return {
    fontSize: px(taille),
    lineHeight: INTERLIGNE.petit,
    width: "max-content",
    maxWidth: px((largeurDuResume(bloc) / VB_W) * sw),
  };
}

/** Compte les lignes que prennent le détail et le résumé une fois posés, et les
 *  remonte au podium (qui en tient compte pour l'échelle). Une personne qui
 *  sort (AnimatePresence) ne dit plus rien : sa remplaçante parle pour la marche. */
function useLignesDeDessous(
  marche: Marche,
  hauteurLigne: number,
  onLignes: (marche: Marche, lignes: Lignes) => void,
) {
  const presente = useIsPresent();
  const refDetail = useRef<HTMLSpanElement | null>(null);
  const refSous = useRef<HTMLSpanElement | null>(null);
  useLayoutEffect(() => {
    if (!presente) return;
    const compte = (el: HTMLElement | null): number =>
      el && el.offsetHeight > 0 && hauteurLigne > 0 ? Math.max(1, Math.round(el.offsetHeight / hauteurLigne)) : 1;
    onLignes(marche, { detail: compte(refDetail.current), sous: compte(refSous.current) });
  });
  return { refDetail, refSous };
}

interface PersonneProps extends Required<Pick<OptionsFormat, "format" | "devise">> {
  bloc: Bloc;
  cadre: Cadre;
  entree: EntreeClassement;
  reduit: boolean;
  suffixe?: string;
  formater?: (valeur: number) => string;
  sousMarche?: PodiumProps["sousMarche"];
  sousMarcheCourt?: PodiumProps["sousMarcheCourt"];
  couleurMontants: NonNullable<PodiumProps["couleurMontants"]>;
  onClick?: (entree: EntreeClassement) => void;
  onLignes: (marche: Marche, lignes: Lignes) => void;
}

function Personne({
  bloc,
  cadre,
  entree,
  reduit,
  format,
  devise,
  suffixe,
  formater,
  sousMarche,
  sousMarcheCourt,
  couleurMontants,
  onClick,
  onLignes,
}: PersonneProps) {
  const t = taillesDe(cadre.sw);
  const hauteurLigne = t.petit * INTERLIGNE.petit;
  const { refDetail, refSous } = useLignesDeDessous(bloc.marche, hauteurLigne, onLignes);
  // L'abrégé ne sert que si la version longue dépasse deux lignes : on la pose,
  // invisible, à la même largeur, et on compte ses lignes (pas de va-et-vient :
  // la décision ne dépend que de la longue).
  const long = sousMarche?.(bloc.marche, entree);
  const court = sousMarche ? sousMarcheCourt?.(bloc.marche, entree) : undefined;
  const refLong = useRef<HTMLSpanElement | null>(null);
  const [abrege, setAbrege] = useState(false);
  useLayoutEffect(() => {
    const el = refLong.current;
    if (court === undefined || !el || el.offsetHeight === 0 || hauteurLigne <= 0) return;
    const voulu = Math.round(el.offsetHeight / hauteurLigne) > 2;
    if (voulu !== abrege) setAbrege(voulu);
  });
  const retard = bloc.retard + 0.34;
  const premier = bloc.marche === 1;
  const cache = entree.montant === null;

  const anime = cadre.mesuree && !reduit;
  const valeur = useCountUp(entree.montant ?? 0, {
    depuis: anime ? 0 : undefined,
    dureeMs: anime ? 950 : 0,
    retardMs: anime ? Math.round(retard * 1000) : 0,
  });
  const affiche = Number.isInteger(entree.montant ?? 0)
    ? Math.round(valeur)
    : Math.round(valeur * 100) / 100;

  const contenu: ReactNode = (
    <>
      {premier && <Trophee cote={t.trophee} reduit={reduit} retard={retard + 0.22} />}
      <Tuile icone={entree.icone} nom={entree.nom} taille={px(premier ? t.tuile1 : t.tuile)}>
        {entree.estMoi && <span className="cl-toi">Toi</span>}
      </Tuile>
      <span
        className="max-w-full truncate font-semibold"
        style={{ fontSize: px(t.nom), lineHeight: INTERLIGNE.nom, marginTop: px(t.margeNom) }}
      >
        {entree.nom}
      </span>
      {entree.sousNom && (
        <span
          className="max-w-full truncate"
          style={{ fontSize: px(t.sousNom), lineHeight: INTERLIGNE.nom, opacity: 0.6 }}
        >
          {entree.sousNom}
        </span>
      )}
      {entree.exAequo && (
        <span
          className="cl-etiquette"
          style={{ fontSize: px(t.etiquette), lineHeight: INTERLIGNE.nom, opacity: 0.7 }}
        >
          ex æquo
        </span>
      )}
      <span
        className={cn(
          "cl-montant font-semibold leading-none",
          cache && "opacity-50",
          !cache && couleurMontants === "vert" && "cl-montant-vert",
        )}
        style={{ fontSize: px(t.montant) }}
      >
        {cache ? "—" : formatAvec(affiche, { format, devise, formater })}
        {!cache && suffixe && (
          <span className="font-normal" style={{ fontSize: "0.7em", opacity: 0.75 }}>
            {suffixe}
          </span>
        )}
      </span>
      {entree.detail && (
        <span
          ref={refDetail}
          className="text-balance"
          style={{ ...styleDessous(bloc, cadre.sw, t.petit), opacity: 0.78 }}
        >
          {entree.detail}
        </span>
      )}
      {sousMarche && (
        <span ref={refSous} className="text-balance" style={styleDessous(bloc, cadre.sw, t.petit)}>
          {abrege && court !== undefined ? court : long}
        </span>
      )}
      {court !== undefined && (
        <span
          ref={refLong}
          aria-hidden="true"
          className="text-balance"
          style={{
            ...styleDessous(bloc, cadre.sw, t.petit),
            position: "absolute",
            left: "50%",
            top: 0,
            transform: "translateX(-50%)",
            visibility: "hidden",
            pointerEvents: "none",
          }}
        >
          {long}
        </span>
      )}
    </>
  );

  const anim = {
    initial: reduit ? false : { opacity: 0, x: "-50%", y: 14 },
    animate: { opacity: 1, x: "-50%", y: 0 },
    exit: { opacity: 0, x: "-50%", y: 10, transition: { duration: 0.28 } },
    transition: { delay: retard, duration: 0.45, ease: COURBE },
  } as const;
  const style = placeDe(bloc, cadre);

  return onClick ? (
    <motion.button
      type="button"
      role="listitem"
      className="cl-personne"
      data-marche={bloc.marche}
      data-cliquable="true"
      style={style}
      onClick={() => onClick(entree)}
      {...anim}
    >
      {contenu}
    </motion.button>
  ) : (
    <motion.div
      role="listitem"
      className="cl-personne"
      data-marche={bloc.marche}
      data-cliquable="false"
      style={style}
      {...anim}
    >
      {contenu}
    </motion.div>
  );
}

/** Une marche sans personne : une tuile en pointillé, et le mot pour le dire. */
function PlaceLibre({
  bloc,
  cadre,
  reduit,
  texte,
  sous,
  onLignes,
}: {
  bloc: Bloc;
  cadre: Cadre;
  reduit: boolean;
  texte: string;
  sous?: ReactNode;
  onLignes: (marche: Marche, lignes: Lignes) => void;
}) {
  const t = taillesDe(cadre.sw);
  const { refSous } = useLignesDeDessous(bloc.marche, t.petit * INTERLIGNE.petit, onLignes);
  const retard = bloc.retard + 0.34;
  return (
    <motion.div
      role="listitem"
      className="cl-personne"
      data-marche={bloc.marche}
      data-cliquable="false"
      data-libre="true"
      style={placeDe(bloc, cadre)}
      initial={reduit ? false : { opacity: 0, x: "-50%", y: 14 }}
      animate={{ opacity: 1, x: "-50%", y: 0 }}
      exit={{ opacity: 0, x: "-50%", y: 10, transition: { duration: 0.28 } }}
      transition={{ delay: retard, duration: 0.45, ease: COURBE }}
    >
      <Tuile
        className="cl-tuile-libre"
        nom="?"
        taille={px(bloc.marche === 1 ? t.tuile1 : t.tuile)}
        icone={<span style={{ opacity: 0.45, fontSize: "1.4em" }}>?</span>}
      />
      <span
        className="max-w-full truncate font-semibold"
        style={{ fontSize: px(t.nom), lineHeight: INTERLIGNE.nom, marginTop: px(t.margeNom), opacity: 0.55 }}
      >
        {texte}
      </span>
      {sous && (
        <span ref={refSous} className="text-balance" style={styleDessous(bloc, cadre.sw, t.petit)}>
          {sous}
        </span>
      )}
    </motion.div>
  );
}

function Trophee({ cote, reduit, retard }: { cote: number; reduit: boolean; retard: number }) {
  return (
    <motion.span
      className="block"
      style={{
        color: "var(--ui-lumiere)",
        filter: "drop-shadow(0 0 10px color-mix(in srgb, var(--ui-lumiere) 70%, transparent))",
      }}
      initial={reduit ? false : { opacity: 0, y: -30, scale: 0.6 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 320, damping: 14, delay: retard }}
    >
      <span className="cl-flotte block" style={{ width: px(cote), height: px(cote) }}>
        <Trophy size="100%" weight="fill" />
      </span>
    </motion.span>
  );
}
